// Copyright (c) 2021 Deere & Company

import * as fetchService from '../../../shared/services/fetch';
import { getBatchBasePath } from '../../../shared/services/get-batch-base-path';
import { handleError } from '../../../shared/services/error-handlers';


const batchBasePath = getBatchBasePath();
const batchService = getBatchService();

/**
 * Get batch types and their units from the database
 * @returns {Promise} - Response from the API with batch types and units
 */
export async function getBatchUnits () {

    const endPoint = `${batchBasePath}/${batchService}/schedule/batchtype-units`;

    const options = {
        method: 'GET'
    };

    try {

        const response = await fetchService.fetch( endPoint, options );

        return response.json();

    } catch ( error ) {

        return handleError( error );

    }

}

/**
 * Map batch type to corresponding endpoint
 * @param {string} batchType - The batch type value
 * @returns {string} - The endpoint path for the batch type
 */
function getBatchTypeEndpoint ( batchType ) {
    const endpointMap = {
        'DAILY': 'trigger-daily-lambda',
        'OF87': 'of87-trigger-script',
        'AOPS': 'aops-trigger-script',
        'MONTH_END': 'monthly-trigger-script',
        'MONTH_POOL': 'monthly-pool-trigger-script'
    };
    
    return endpointMap[batchType] || 'trigger-script';
}

/**
 * Map frontend batch type to database batch type for history queries
 * @param {string} batchType - The batch type value from frontend
 * @returns {string} - The batch type value in database
 */
function mapBatchTypeToDatabase ( batchType ) {
    const dbMap = {
        'DAILY': 'Daily',
        'MONTH_END': 'Monthly',
        'MONTH_POOL': 'MPool',
        'AOPS': 'AOPS',
        'OF87': 'OF87'
    };
    
    return dbMap[batchType] || batchType;
}

/**
 * Trigger batch operation
 * @param {Object} batchData - Contains batchType and unitName
 * @returns {Promise} - Response from the API
 */
export async function triggerBatch ( batchData ) {

    const endpoint = getBatchTypeEndpoint( batchData.batchType );    
    const endPointURL = `${batchBasePath}/${batchService}/schedule/${endpoint}?unit=${encodeURIComponent( batchData.unitName )}&triggerBy=${encodeURIComponent( batchData.userFirstName )}`;
    
    console.log( batchData.batchType + ' - Triggering the batch via endPoint url: ' + endPointURL);
    
    const options = {
        method: 'POST'
    };

    try {

        const response = await fetchService.fetch( endPointURL, options );

        return response.json();

    } catch ( error ) {

        return handleError( error );

    }

}

/**
 * Get batch execution history
 * @param {string} batchType - Optional batch type filter
 * @param {string} unit - Optional unit filter
 * @returns {Promise} - Response from the API with batch history
 */
export async function getBatchHistory ( batchType = '', unit = '' ) {

    // Map frontend batch type to database format
    const dbBatchType = batchType ? mapBatchTypeToDatabase( batchType ) : '';

    const params = [];
    params.push( `batchType=${encodeURIComponent( dbBatchType )}` );
    params.push( `unit=${encodeURIComponent( unit )}` );
    
    const endPoint = `${batchBasePath}/${batchService}/schedule/batch-history?${params.join( '&' )}`;

    const options = {
        method: 'GET'
    };

    try {

        const response = await fetchService.fetch( endPoint, options );
        const buffer = await response.arrayBuffer();
        const text = new TextDecoder('utf-8').decode(buffer);
        
        // Backend sends valid JavaScript but not strict JSON
        // Use eval as workaround (safe here - our own backend)
        const data = eval(`(${text})`);
        
        return data;

    } catch ( error ) {

        console.error('Error loading batch history:', error.message);
        return [];

    }

}

/**
 * Get the batch service name
 * @returns {string} - The batch service name
 */
export function getBatchService () {

    return 'atp-batch-service-1.0.0';

}
